import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, Plus, Trash2 } from 'lucide-react';
import { eventsApi } from '@/features/events/api';
import { qrGroupApi, type QrGroup } from './api';
import { queryKeys } from '@/lib/api/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';

/**
 * Shared-QR manager for an event: create a single QR shared by 2+ stations
 * (each keeps its own xotskis), list existing shared QRs, show/print the code,
 * and delete. Lives in the event PLAN tab.
 */
export function SharedQrManager({
  eventId,
  brandId,
}: {
  eventId: number;
  brandId: number;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);

  const groupsQ = useQuery({
    queryKey: queryKeys.qrGroups.byEvent(eventId),
    queryFn: () => qrGroupApi.list(eventId),
    enabled: Number.isFinite(eventId),
  });
  const groups = groupsQ.data ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-4 w-4 text-[var(--color-accent-ink)]" />
              {t('sharedQr.manager.title')}
            </CardTitle>
            <p className="text-xs text-[var(--color-fg-muted)]">{t('sharedQr.manager.subtitle')}</p>
          </div>
          <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setOpen(true)}>
            {t('sharedQr.manager.create')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {groups.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[var(--color-xk-border)] p-4 text-center text-xs text-[var(--color-fg-muted)]">
            {t('sharedQr.manager.empty')}
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {groups.map((g) => (
              <SharedQrCard key={g.id} group={g} eventId={eventId} />
            ))}
          </div>
        )}
      </CardContent>

      {open && (
        <CreateSharedQrDialog
          open={open}
          onOpenChange={setOpen}
          eventId={eventId}
          brandId={brandId}
        />
      )}
    </Card>
  );
}

function SharedQrCard({ group, eventId }: { group: QrGroup; eventId: number }) {
  const t = useT();
  const queryClient = useQueryClient();
  const url = `${window.location.origin}/sg/${group.id}`;

  const remove = useMutation({
    mutationFn: () => qrGroupApi.remove(group.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.qrGroups.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.byEvent(eventId) });
      toast({ tone: 'success', title: t('sharedQr.manager.removed') });
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('sharedQr.manager.removeFailed'), description: e.message }),
  });

  return (
    <div className="flex gap-3 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-3">
      <div className="rounded-lg bg-white p-1.5">
        <QRCodeSVG value={url} size={72} level="M" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-[var(--color-fg-primary)]">{group.name}</p>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="block truncate text-[11px] text-[var(--color-accent-ink)] hover:underline"
        >
          {url}
        </a>
        {group.stations && group.stations.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {group.stations.map((s) => (
              <span
                key={s.id}
                className="max-w-[130px] truncate rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-bg-base)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-fg-muted)]"
              >
                {s.name}
              </span>
            ))}
          </div>
        )}
        <div className="mt-2 flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              navigator.clipboard?.writeText(url).catch(() => {});
              toast({ tone: 'success', title: t('sharedQr.manager.copied') });
            }}
          >
            {t('sharedQr.manager.copy')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Trash2 className="h-3.5 w-3.5" />}
            loading={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {t('sharedQr.manager.delete')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateSharedQrDialog({
  open,
  onOpenChange,
  eventId,
  brandId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  eventId: number;
  brandId: number;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<number[]>([]);

  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byEvent(eventId),
    queryFn: () => eventsApi.listStations({ eventId }),
    enabled: open,
  });
  const stations = stationsQ.data?.items ?? [];

  /*
    Qué encuesta corre este QR.

    Sale de las propias estaciones: `surveysId` es la columna que Xotski
    mantiene al desplegar, o sea la encuesta que de verdad se está sirviendo en
    ellas. Antes se preguntaba a `bs_event`, que guardaba la plantilla elegida
    en el editor anterior — y desde que el despliegue es quien manda, ese dato
    ya no se escribe: el QR compartido podía acabar apuntando a una encuesta
    distinta de la que responden las estaciones que agrupa.

    Se miran primero las seleccionadas, porque son las que el QR va a agrupar.
  */
  const elegidas = stations.filter((s) => selected.includes(s.id));
  const surveysId =
    (elegidas.length ? elegidas : stations).find((s) => s.surveysId)?.surveysId ?? null;

  const toggle = (id: number) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const create = useMutation({
    mutationFn: () =>
      qrGroupApi.create({
        eventId,
        name: name.trim() || t('sharedQr.manager.defaultName'),
        surveysId,
        stationIds: selected,
        companyId: brandId || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.qrGroups.byEvent(eventId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.byEvent(eventId) });
      toast({ tone: 'success', title: t('sharedQr.manager.createdToast') });
      onOpenChange(false);
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('sharedQr.manager.createFailed'), description: e.message }),
  });

  const canCreate = selected.length >= 2 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('sharedQr.manager.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('sharedQr.manager.dialogBody')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block">
            <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">
              {t('sharedQr.manager.nameLabel')}
            </div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('sharedQr.manager.defaultName')} />
          </label>
          <div>
            <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">
              {t('sharedQr.manager.pickStations')}
            </div>
            {stations.length < 2 ? (
              <p className="rounded-lg border border-dashed border-[var(--color-xk-border)] p-3 text-xs text-[var(--color-fg-muted)]">
                {t('sharedQr.manager.needTwo')}
              </p>
            ) : (
              <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1 scrollbar-thin">
                {stations.map((s) => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-2"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(s.id)}
                      onChange={() => toggle(s.id)}
                      className="accent-[var(--color-xk-accent)]"
                    />
                    <span className="text-sm text-[var(--color-fg-primary)]">{s.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <DialogClose asChild>
              <button
                type="button"
                className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
              >
                {t('sharedQr.manager.cancel')}
              </button>
            </DialogClose>
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!canCreate}>
              {t('sharedQr.manager.createBtn')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
